<template>
  <div v-if="isSkuFilter || (!isSkuFilter && options.length > 1)">
    <Listbox v-slot="{ open }" v-model="localModel" as="div">
      <div class="flex gap-2">
        <!-- Label -->
        <ListboxLabel
          v-if="label"
          class="relative flex items-center gap-1 font-semibold leading-6 text-gray-900 md:font-bold"
        >
          <span>{{ label }}</span>
        </ListboxLabel>

        <!-- Tooltip (Don't add hover on mobile because it breaks safari compatability) -->
        <DefaultTooltip
          v-if="showDefaultTooltip"
          ref="popper"
          :hover="false"
          class="md:group text-sm font-normal"
          @touchstart="openPopper"
        >
          <template #default>
            <FontAwesomeIcon
              :icon="{ prefix: 'fas', iconName: 'info-circle' }"
              class="h-5 w-5 cursor-pointer text-gray md:group-hover:text-black"
            />
          </template>

          <template #content>
            <span v-html="localModel.value.description" />
          </template>
        </DefaultTooltip>
      </div>

      <div class="relative mt-1">
        <ListboxButton
          :disabled="loading || disabled"
          class="relative w-full cursor-pointer rounded-md border border-gray-400 bg-white py-2 pl-4 pr-8 text-left disabled:opacity-70 sm:leading-6"
        >
          <FilterDropdownButton
            :selected-option="modelValue"
            v-bind="{
              open,
              index,
              loading,
              isShowingPredefinedShowText,
              showPrices,
            }"
          />
        </ListboxButton>

        <Transition
          leave-active-class="transition ease-in duration-100"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <ListboxOptions
            :class="{
              'bottom-full rounded-t-md border-b-0': showAbove,
              'rounded-b-md border-t-0': !showAbove,
            }"
            class="absolute z-10 mt-0 max-h-60 w-full overflow-auto border border-gray-400 bg-white text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
          >
            <FilterDropdownOption
              v-for="option in options"
              :key="option.value"
              :selected-option="modelValue"
              :is-showing-predefined-show-text="showPredefinedShowText"
              v-bind="{
                option,
                disabled,
                showPrices,
                index,
              }"
            />
          </ListboxOptions>
        </Transition>
      </div>
    </Listbox>
  </div>
</template>

<script setup lang="ts">
import {
  Listbox,
  ListboxButton,
  ListboxLabel,
  ListboxOptions,
} from '@headlessui/vue';
import { computed, ComputedRef, ModelRef, PropType, ref, Ref } from 'vue';
import type { SelectorOption } from '@/types/Configurator/SelectorOption';
import DefaultTooltip from '@/components/Tooltips/DefaultTooltip.vue';
import Popper from 'vue3-popper';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import FilterDropdownButton from '@/pages/ProductPage/partials/ProductFilters/FilterDropdownButton.vue';
import FilterDropdownOption from '@/pages/ProductPage/partials/ProductFilters/FilterDropdownOption.vue';

const emit = defineEmits(['update:model-value']);

const props = defineProps({
  options: {
    type: Array as PropType<SelectorOption[]>,
    required: true,
  },

  index: {
    type: [String, Number] as PropType<string | number>,
    required: false,
    default: undefined,
  },

  label: {
    type: String,
    required: false,
    default: undefined,
  },

  showTooltip: {
    type: Boolean,
    required: false,
    default: true,
  },

  loading: {
    type: Boolean,
    required: false,
    default: false,
  },

  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },

  isSkuFilter: {
    type: Boolean,
    required: false,
    default: false,
  },

  showPrices: {
    type: Boolean,
    required: false,
    default: true,
  },

  showAbove: {
    type: Boolean,
    required: false,
    default: false,
  },

  showPredefinedShowText: {
    type: Boolean,
    required: false,
    default: true,
  },
});

const popper: Ref<typeof Popper | null> = ref(null);

const model: ModelRef<SelectorOption> = defineModel({
  type: Object as PropType<SelectorOption>,
  required: true,
});

const localModel = computed({
  get() {
    return model;
  },
  set(newValue) {
    emit('update:model-value', newValue);
  },
});

const isShowingPredefinedShowText: ComputedRef<boolean> = computed(
  () => props.showPredefinedShowText
);

// Prevent rendering tooltips when the Froala WYSIWYG editor returns empty content like <p><br></p>.
function isWysiwygEmpty(html?: string | null): boolean {
  const normalized = (html ?? '').replace(/\s+/g, '').toLowerCase();
  return ['', '<p><br></p>', '<p>&nbsp;</p>', '<p></p>'].includes(normalized);
}

const showDefaultTooltip: ComputedRef<boolean> = computed(
  () =>
    props.showTooltip && !isWysiwygEmpty(localModel.value.value?.description)
);

function openPopper() {
  popper.value?.$el.click();
}
</script>
